import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  Search, LayoutGrid, Activity, Image, Layers, Sparkles, 
  Trophy, Swords, Gift, ArrowRight, Zap, TrendingUp, Star, Flame, Newspaper, Crown, GraduationCap, BookOpen, DollarSign, Target, Users, Code2, Share2, Workflow, Package, Gem, Briefcase, CalendarDays, CreditCard, HelpCircle, MessageSquareWarning
} from 'lucide-react';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface Tool {
  id: string;
  name: string;
  slug: string;
  short_description: string;
  logo_url: string | null;
  pricing: string;
}

interface TrendingTool extends Tool {
  view_count: number;
}

function ToolCard({ tool, showViews }: { tool: Tool | TrendingTool; showViews?: boolean }) {
  return (
    <NavigationMenuLink asChild>
      <Link 
        to={`/tool/${tool.slug}`} 
        className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-accent transition-colors group"
      >
        <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0 overflow-hidden">
          {tool.logo_url ? (
            <img 
              src={tool.logo_url} 
              alt={tool.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = `<span class="text-sm font-bold text-muted-foreground">${tool.name.charAt(0)}</span>`;
              }}
            />
          ) : (
            <span className="text-sm font-bold text-muted-foreground">{tool.name.charAt(0)}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors truncate">
              {tool.name}
            </span>
            {showViews && 'view_count' in tool && (
              <Badge variant="outline" className="text-[10px] shrink-0 text-orange-500 border-orange-500/30">
                {tool.view_count} views
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
            {tool.short_description}
          </p>
        </div>
      </Link>
    </NavigationMenuLink>
  );
}

function ToolCardSkeleton() {
  return (
    <div className="flex items-start gap-3 p-2.5">
      <Skeleton className="w-9 h-9 rounded-lg" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-full" />
      </div>
    </div>
  );
}

interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  source_name: string;
  published_at: string;
}

export function DiscoverMegaMenu() {
  const { data: featuredTools, isLoading: featuredLoading } = useQuery({
    queryKey: ['featured-tools-nav'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('id, name, slug, short_description, logo_url, pricing')
        .eq('is_featured', true)
        .eq('status', 'approved')
        .limit(2);
      if (error) throw error;
      return data as Tool[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: trendingTools, isLoading: trendingLoading } = useQuery({
    queryKey: ['trending-tools-nav'],
    queryFn: async () => {
      // Get views from the last 7 days
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const { data: analytics, error: analyticsError } = await supabase
        .from('tool_analytics')
        .select('tool_id')
        .eq('event_type', 'view')
        .gte('created_at', weekAgo.toISOString());
      
      if (analyticsError) throw analyticsError;
      
      // Count views per tool
      const viewCounts: Record<string, number> = {};
      analytics?.forEach(a => {
        viewCounts[a.tool_id] = (viewCounts[a.tool_id] || 0) + 1;
      });
      
      // Get top 3 tool IDs by view count
      const topToolIds = Object.entries(viewCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([id]) => id);
      
      if (topToolIds.length === 0) return [];
      
      // Fetch tool details
      const { data: tools, error: toolsError } = await supabase
        .from('tools')
        .select('id, name, slug, short_description, logo_url, pricing')
        .in('id', topToolIds)
        .eq('status', 'approved');
      
      if (toolsError) throw toolsError;
      
      // Add view counts and sort
      return (tools || [])
        .map(tool => ({
          ...tool,
          view_count: viewCounts[tool.id] || 0
        }))
        .sort((a, b) => b.view_count - a.view_count) as TrendingTool[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: latestNews, isLoading: newsLoading } = useQuery({
    queryKey: ['latest-news-nav'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_articles')
        .select('id, title, slug, source_name, published_at')
        .order('published_at', { ascending: false })
        .limit(3);
      if (error) throw error;
      return data as NewsArticle[];
    },
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="grid grid-cols-[180px_1fr] gap-0 p-0 w-[720px] bg-popover">
      {/* Left Column - Navigation */}
      <div className="bg-muted/50 p-3 rounded-l-md border-r border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
          Browse
        </p>
        <div className="space-y-1">
          <NavigationMenuLink asChild>
            <Link to="/tools" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Search className="h-4 w-4 text-primary" />
              Explore All Tools
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/categories" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <LayoutGrid className="h-4 w-4 text-primary" />
              Categories
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/skills" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Sparkles className="h-4 w-4 text-primary" /> AI Skills
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/news" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Newspaper className="h-4 w-4 text-primary" />
              AI News
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/jobs" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Briefcase className="h-4 w-4 text-blue-500" />
              AI Jobs
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/events" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <CalendarDays className="h-4 w-4 text-rose-500" />
              Events
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/wall-of-fame" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Crown className="h-4 w-4 text-yellow-500" />
              Wall of Fame
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/ai-learning" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <GraduationCap className="h-4 w-4 text-violet-500" />
              AI Learning
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/income-blueprints" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <DollarSign className="h-4 w-4 text-emerald-500" />
              Income Blueprints
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/challenges" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Target className="h-4 w-4 text-amber-500" />
              30-Day Challenges
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/income-reports" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Users className="h-4 w-4 text-primary" />
              Success Stories
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/my-learning" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <BookOpen className="h-4 w-4 text-emerald-500" />
              My Learning
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/health" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Activity className="h-4 w-4 text-muted-foreground" />
              Tool Health
            </Link>
          </NavigationMenuLink>
        </div>

        <div className="mt-4 pt-3 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
            Quick Actions
          </p>
          <NavigationMenuLink asChild>
            <Link to="/search" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Zap className="h-4 w-4 text-amber-500" />
              Quick Search
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/vibe-compare" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              Compare Tools
            </Link>
          </NavigationMenuLink>
        </div>
      </div>

      {/* Right Column - Featured, Trending & News */}
      <div className="p-3 grid grid-cols-3 gap-3">
        {/* Featured Tools */}
        <div>
          <div className="flex items-center justify-between px-2 mb-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Star className="h-3 w-3 text-amber-500" />
              Featured
            </p>
          </div>
          <div className="space-y-0.5">
            {featuredLoading ? (
              <>
                <ToolCardSkeleton />
                <ToolCardSkeleton />
              </>
            ) : featuredTools?.length ? (
              featuredTools.map((tool) => (
                <ToolCard key={tool.id} tool={tool} />
              ))
            ) : (
              <p className="text-xs text-muted-foreground px-2 py-2">No featured tools</p>
            )}
          </div>
        </div>

        {/* Trending Tools */}
        <div>
          <div className="flex items-center justify-between px-2 mb-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Flame className="h-3 w-3 text-orange-500" />
              Trending
            </p>
          </div>
          <div className="space-y-0.5">
            {trendingLoading ? (
              <>
                <ToolCardSkeleton />
                <ToolCardSkeleton />
              </>
            ) : trendingTools?.length ? (
              trendingTools.slice(0, 2).map((tool) => (
                <ToolCard key={tool.id} tool={tool} showViews />
              ))
            ) : (
              <p className="text-xs text-muted-foreground px-2 py-2">No trending data yet</p>
            )}
          </div>
        </div>

        {/* Latest News */}
        <div>
          <div className="flex items-center justify-between px-2 mb-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Newspaper className="h-3 w-3 text-blue-500" />
              AI News
            </p>
          </div>
          <div className="space-y-0.5">
            {newsLoading ? (
              <>
                <ToolCardSkeleton />
                <ToolCardSkeleton />
              </>
            ) : latestNews?.length ? (
              latestNews.map((article) => (
                <NavigationMenuLink asChild key={article.id}>
                  <Link 
                    to={`/news/${article.slug}`} 
                    className="flex flex-col gap-0.5 p-2.5 rounded-lg hover:bg-accent transition-colors group"
                  >
                    <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {article.title}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {article.source_name}
                    </span>
                  </Link>
                </NavigationMenuLink>
              ))
            ) : (
              <p className="text-xs text-muted-foreground px-2 py-2">No news yet</p>
            )}
          </div>
        </div>

        {/* View All Link */}
        <div className="col-span-3 pt-2 border-t border-border mt-1">
          <Link to="/tools" className="flex items-center justify-center gap-1 text-xs text-primary hover:underline py-1">
            Explore all tools <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Wall of Fame Banner */}
        <div className="col-span-3 mt-2">
          <Link 
            to="/wall-of-fame" 
            className="block p-3 rounded-lg bg-gradient-to-r from-yellow-500/10 via-amber-500/10 to-orange-500/10 border border-yellow-500/20 hover:border-yellow-500/40 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-foreground group-hover:text-yellow-600 transition-colors">
                    Wall of Fame
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Showcase your AI tool to people searching for AI
                  </p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-yellow-500 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

export function CreateMegaMenu() {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-0 p-0 w-[420px] bg-popover">
      {/* Left Column - Tools */}
      <div className="bg-muted/50 p-3 rounded-l-md border-r border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
          Builders
        </p>
        <div className="space-y-1">
          <NavigationMenuLink asChild>
            <Link to="/carousel-builder" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Image className="h-4 w-4 text-primary" />
              Carousel Builder
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/social-cards" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Share2 className="h-4 w-4 text-pink-500" />
              Social Media Tools
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/stack-builder" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Layers className="h-4 w-4 text-primary" />
              Stack Builder
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/my-stack" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Layers className="h-4 w-4 text-violet-500" />
              My AI Stacks
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/premium" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm">
              <Gem className="h-4 w-4 text-amber-500" />
              Premium
            </Link>
          </NavigationMenuLink>
        </div>
      </div>

      {/* Right Column */}
      <div className="p-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
          Resources
        </p>
        <div className="space-y-1">
          <NavigationMenuLink asChild>
            <Link to="/digital-products" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors group">
              <Package className="h-4 w-4 text-emerald-500 mt-0.5" />
              <div>
                <span className="text-sm font-medium">Digital Products</span>
                <p className="text-xs text-muted-foreground">Ebooks, prompts & more</p>
              </div>
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/templates" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors group">
              <Workflow className="h-4 w-4 text-primary mt-0.5" />
              <div>
                <span className="text-sm font-medium">AI Workflow Templates</span>
                <p className="text-xs text-muted-foreground">Buy & sell workflow templates</p>
              </div>
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/carousel-templates" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors group">
              <LayoutGrid className="h-4 w-4 text-violet-500 mt-0.5" />
              <div>
                <span className="text-sm font-medium">Carousel Templates</span>
                <p className="text-xs text-muted-foreground">Pre-built carousel designs</p>
              </div>
            </Link>
          </NavigationMenuLink>
          <NavigationMenuLink asChild>
            <Link to="/submit" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors group">
              <Sparkles className="h-4 w-4 text-amber-500 mt-0.5" />
              <div>
                <span className="text-sm font-medium">Submit a Tool</span>
                <p className="text-xs text-muted-foreground">Add your AI tool to the directory</p>
              </div>
            </Link>
          </NavigationMenuLink>
        </div>
      </div>
    </div>
  );
}

export function CommunityMegaMenu() {
  return (
    <div className="grid grid-cols-2 gap-0 p-3 w-[420px] bg-popover">
      <div className="space-y-1 pr-3 border-r border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
          Compete
        </p>
        <NavigationMenuLink asChild>
          <Link to="/leaderboard" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <Trophy className="h-4 w-4 text-amber-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium">Leaderboard</span>
              <p className="text-xs text-muted-foreground">Top voted tools</p>
            </div>
          </Link>
        </NavigationMenuLink>
        <NavigationMenuLink asChild>
          <Link to="/battles" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <Swords className="h-4 w-4 text-rose-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium">Tool Battles</span>
              <p className="text-xs text-muted-foreground">Vote in head-to-head</p>
            </div>
          </Link>
        </NavigationMenuLink>
        <NavigationMenuLink asChild>
          <Link to="/roast-arena" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <MessageSquareWarning className="h-4 w-4 text-orange-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium">Roast Arena</span>
              <p className="text-xs text-muted-foreground">AI tool roasts</p>
            </div>
          </Link>
        </NavigationMenuLink>
      </div>

      <div className="space-y-1 pl-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
          Engage
        </p>
        <NavigationMenuLink asChild>
          <Link to="/marketplace" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <Code2 className="h-4 w-4 text-primary mt-0.5" />
            <div>
              <span className="text-sm font-medium">AI Creators</span>
              <p className="text-xs text-muted-foreground">Hire AI professionals</p>
            </div>
          </Link>
        </NavigationMenuLink>
        <NavigationMenuLink asChild>
          <Link to="/referrals" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <Gift className="h-4 w-4 text-emerald-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium">Referrals</span>
              <p className="text-xs text-muted-foreground">Earn rewards</p>
            </div>
          </Link>
        </NavigationMenuLink>
        <NavigationMenuLink asChild>
          <Link to="/post-job" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <Briefcase className="h-4 w-4 text-blue-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium">Post a Job</span>
              <p className="text-xs text-muted-foreground">Hire AI talent</p>
            </div>
          </Link>
        </NavigationMenuLink>
        <NavigationMenuLink asChild>
          <Link to="/events/submit" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <CalendarDays className="h-4 w-4 text-rose-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium">Submit Event</span>
              <p className="text-xs text-muted-foreground">List your AI event</p>
            </div>
          </Link>
        </NavigationMenuLink>
        <NavigationMenuLink asChild>
          <Link to="/pricing" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <CreditCard className="h-4 w-4 text-primary mt-0.5" />
            <div>
              <span className="text-sm font-medium">Pricing</span>
              <p className="text-xs text-muted-foreground">Plans & features</p>
            </div>
          </Link>
        </NavigationMenuLink>
        <NavigationMenuLink asChild>
          <Link to="/help-center" className="flex items-start gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors">
            <HelpCircle className="h-4 w-4 text-amber-500 mt-0.5" />
            <div>
              <span className="text-sm font-medium">Help Center</span>
              <p className="text-xs text-muted-foreground">FAQs & support</p>
            </div>
          </Link>
        </NavigationMenuLink>
      </div>
    </div>
  );
}
